import adminRequest from './adminRequest';
import { travelAdminJson } from './parseTravelAdminJson';
import axios from 'axios';
import { ADMIN_API } from '../constants/endpoints';
import { ADMIN_STORAGE_KEYS } from '../constants/storageKeys';
import { ApiError } from './createClient';
import { downloadBlob, parseContentDispositionFileName } from '../utils/downloadBlob';

/** 後台管理 API（僅 admin 應用使用，走 ADMIN_API） */
export const adminApi = {
  login: (data) => adminRequest.post(`${ADMIN_API}/adminEnt/login`, data),

  getVersionInfo: () => adminRequest.get(`${ADMIN_API}/getVersionInfo`),

  googleVerifyConfig: () =>
    adminRequest.get(`${ADMIN_API}/adminEnt/google-verify-config`),

  memberPage: (params) =>
    adminRequest.get(`${ADMIN_API}/member/page-query`, { params }),

  memberDetail: (id) =>
    adminRequest.post(`${ADMIN_API}/member/detail`, { id }),

  memberUpdate: (data) =>
    adminRequest.post(`${ADMIN_API}/member/update`, data),

  memberBatchUpdate: (data) =>
    adminRequest.post(`${ADMIN_API}/member/update/batchStatusAndRemark`, data),

  memberResetPassword: (data) =>
    adminRequest.post(`${ADMIN_API}/member/update/password`, data),

  memberDisable: (id) =>
    adminRequest.post(`${ADMIN_API}/member/delete`, null, { params: { id } }),

  memberImMute: (data) =>
    adminRequest.post(`${ADMIN_API}/member/im-mute`, data),

  memberInviterTree: (params) =>
    adminRequest.get(`${ADMIN_API}/member/inviter-tree`, { params }),

  memberRegeneratePromotionCode: (id) =>
    adminRequest.post(`${ADMIN_API}/member/regenerate-promotion-code`, null, { params: { id } }),

  memberRefreshDailyReport: (data) =>
    adminRequest.post(`${ADMIN_API}/member/createDailyUserReport`, data),

  memberLoginWithoutPassword: (memberId) =>
    adminRequest.get(`${ADMIN_API}/member/loginWithoutPassword`, { params: { memberId } }),

  exportMemberCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/member/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_list'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  transactionPage: (params) =>
    adminRequest.get(`${ADMIN_API}/memberTransaction/page`, { params }),

  updateTransactionRemark: (data) =>
    adminRequest.post(`${ADMIN_API}/memberTransaction/update-remark`, data),

  exportTransactionCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/memberTransaction/export/csv-zip`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_transaction'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  adjustBalance: (data) =>
    adminRequest.post(`${ADMIN_API}/memberTransaction/admin/system/adjust`, data),

  depositPage: (params) =>
    adminRequest.get(`${ADMIN_API}/memberDeposit/page`, { params }),

  offlineDepositPage: (params) =>
    adminRequest.get(`${ADMIN_API}/memberDeposit/offline/page`, { params }),

  offlineDepositTotal: (params) =>
    adminRequest.get(`${ADMIN_API}/memberDeposit/offline/total`, { params }),

  exportOfflineDepositCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/memberDeposit/offline/export/csv-zip`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_offline_deposit'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  depositOfflineLock: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/offline/lock`, data),

  depositOfflineAudit: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/offline/audit`, data),

  depositOfflineUpdateAmount: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/offline/updateAmount`, data),

  depositOfflineUpdateRate: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/offline/updateRate`, data),

  onlineDepositPage: (params) =>
    adminRequest.get(`${ADMIN_API}/memberDeposit/online/page`, { params }),

  onlineDepositTotal: (params) =>
    adminRequest.get(`${ADMIN_API}/memberDeposit/online/total`, { params }),

  exportOnlineDepositCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/memberDeposit/online/export/csv-zip`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_online_deposit'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  onlineDepositUpdateToProcessing: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/online/updateToProcessing`, data),

  onlineDepositCheckOrder: (data) =>
    adminRequest.post(`${ADMIN_API}/memberDeposit/online/checkDepositOrder`, data),

  withdrawPage: (params) =>
    adminRequest.get(`${ADMIN_API}/memberWithdraw/page`, { params }),

  withdrawLock: (data) =>
    adminRequest.post(`${ADMIN_API}/memberWithdraw/lock`, data),

  withdrawAudit: (data) =>
    adminRequest.post(`${ADMIN_API}/memberWithdraw/audit`, data),

  withdrawTotal: (params) =>
    adminRequest.get(`${ADMIN_API}/memberWithdraw/total`, { params }),

  exportWithdrawCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/memberWithdraw/export/csv-zip`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_withdraw'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  futuresRoundsPage: (params) =>
    adminRequest.get(`${ADMIN_API}/futures/rounds/page`, { params }),

  futuresOrdersPage: (params) =>
    adminRequest.get(`${ADMIN_API}/futures/orders/page`, { params }),

  exportFuturesOrdersExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/futures/orders/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'futures_order'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  cancelFuturesOrder: (data) =>
    adminRequest.post(`${ADMIN_API}/futures/orders/cancel`, data),

  followOrderPage: (params) =>
    adminRequest.get(`${ADMIN_API}/follow-order/page`, { params }),

  createFollowOrder: (data) =>
    adminRequest.post(`${ADMIN_API}/follow-order`, data),

  disableFollowOrder: (id) =>
    adminRequest.post(`${ADMIN_API}/follow-order/disable`, { id }),

  /** 跟單全域開關（GlobalConfig，tenantConfig/config/global-follow） */
  getGlobalFollowConfig: () =>
    adminRequest.get(`${ADMIN_API}/tenantConfig/config/global-follow`),

  saveGlobalFollowConfig: (patch) =>
    adminRequest.post(`${ADMIN_API}/tenantConfig/save/global-follow`, patch),

  followOrderAvailableRounds: (productId, params = {}) =>
    adminRequest.get(`${ADMIN_API}/follow-order/available-rounds`, {
      params: { productId, pageNo: 1, pageSize: 500, ...params },
    }),

  followCustomizeMembersPage: (params) =>
    adminRequest.get(`${ADMIN_API}/follow-customize-members/page`, { params }),

  batchAddFollowCustomizeMembers: (memberIds) =>
    adminRequest.post(`${ADMIN_API}/follow-customize-members/batch`, { memberIds }),

  batchDeleteFollowCustomizeMembers: (ids) =>
    adminRequest.post(`${ADMIN_API}/follow-customize-members/batch-delete`, { ids }),

  deleteFollowCustomizeMember: (id) =>
    adminRequest.post(`${ADMIN_API}/follow-customize-members/delete`, { id }),

  /** 站點配置：單項讀取（如 itemKey=global） */
  tenantConfigOne: (itemKey) =>
    adminRequest.get(`${ADMIN_API}/tenantConfig/config/one`, { params: { itemKey } }),

  /** 充提參數（PaymentSettingConfig，itemKey=paymentSetting） */
  getPaymentSettingConfig: () =>
    adminRequest.get(`${ADMIN_API}/tenantConfig/config/payment-setting`),

  savePaymentSettingConfig: (content) =>
    adminRequest.post(`${ADMIN_API}/tenantConfig/save/payment-setting`, { content }),

  coinPayTypePage: (params) =>
    adminRequest.get(`${ADMIN_API}/coinPayType/page`, { params }),

  createCoinPayType: (data) =>
    adminRequest.post(`${ADMIN_API}/coinPayType/save`, data),

  updateCoinPayType: (data) =>
    adminRequest.post(`${ADMIN_API}/coinPayType/update`, data),

  removeCoinPayTypes: (ids) =>
    adminRequest.post(`${ADMIN_API}/coinPayType/remove`, ids),

  listMerchants: () =>
    adminRequest.get(`${ADMIN_API}/merchantAdmin/merchant/list`),

  createMerchant: (data) =>
    adminRequest.post(`${ADMIN_API}/merchantAdmin/merchant`, data),

  updateMerchant: (data) =>
    adminRequest.put(`${ADMIN_API}/merchantAdmin/merchant`, data),

  listMerchantChannels: (merchantId) =>
    adminRequest.get(`${ADMIN_API}/merchantAdmin/channel/list`, {
      params: merchantId != null && merchantId !== '' ? { merchantId } : {},
    }),

  createMerchantChannel: (data) =>
    adminRequest.post(`${ADMIN_API}/merchantAdmin/channel`, data),

  updateMerchantChannel: (data) =>
    adminRequest.put(`${ADMIN_API}/merchantAdmin/channel`, data),

  staticIntroPage: (params) =>
    adminRequest.get(`${ADMIN_API}/staticIntro/page`, { params }),

  staticIntroDetail: (id) =>
    adminRequest.get(`${ADMIN_API}/staticIntro/detail`, { params: { id } }),

  createStaticIntro: (data) =>
    adminRequest.post(`${ADMIN_API}/staticIntro/save`, data),

  updateStaticIntro: (data) =>
    adminRequest.post(`${ADMIN_API}/staticIntro/update`, data),

  removeStaticIntro: (id) =>
    adminRequest.post(`${ADMIN_API}/staticIntro/remove`, id),

  toggleStaticIntroEnabled: (data) =>
    adminRequest.post(`${ADMIN_API}/staticIntro/enabled`, data),

  walletAddressPage: (params) =>
    adminRequest.get(`${ADMIN_API}/payment/address-manage/page`, { params }),

  createWalletAddress: (data) =>
    adminRequest.post(`${ADMIN_API}/payment/address-manage/save`, data),

  updateWalletAddress: (data) =>
    adminRequest.post(`${ADMIN_API}/payment/address-manage/update`, data),

  removeWalletAddresses: (ids) =>
    adminRequest.post(`${ADMIN_API}/payment/address-manage/remove`, ids),

  listEnabledLanguages: () =>
    adminRequest.get(`${ADMIN_API}/system/language/list`),

  listAllLanguages: () =>
    adminRequest.get(`${ADMIN_API}/system/language/page-query`, {
      params: { pageNo: 1, pageSize: 500, status: -1 },
    }),

  updateLanguage: (data) =>
    adminRequest.post(`${ADMIN_API}/system/language/update`, data),

  listCountries: () =>
    adminRequest.get(`${ADMIN_API}/system/country/country-list`),

  listAllCountries: (splitAreaCode = true) =>
    adminRequest.get(`${ADMIN_API}/system/country/list-all`, {
      params: { splitAreaCode },
    }),

  updateCountry: (data) =>
    adminRequest.post(`${ADMIN_API}/system/country/update`, data),

  uploadOssImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Result.data = objectKey (path only; no OSS bucket host)
    return adminRequest.post(`${ADMIN_API}/common/upload/oss/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadOssVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return adminRequest.post(`${ADMIN_API}/common/upload/oss/video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** 站點配置：單項保存（content 可局部更新，後端會 merge） */
  saveTenantConfig: (itemKey, content) =>
    adminRequest.post(`${ADMIN_API}/tenantConfig/save/one`, { itemKey, content }),

  configFuturesRounds: (data) =>
    adminRequest.post(`${ADMIN_API}/futures/rounds/config`, data),

  futuresRoundConfigDetail: (roundCode) =>
    adminRequest.get(`${ADMIN_API}/futures/rounds/config-detail`, {
      params: { roundCode },
    }),

  settlePendingRound: (data) =>
    adminRequest.post(`${ADMIN_API}/futures/rounds/settle-pending`, data),

  syncFuturesRoundResults: (data) =>
    adminRequest.post(`${ADMIN_API}/futures/rounds/sync-results`, data),

  /** 玩法商品 */
  orderProductPage: (params) =>
    adminRequest.get(`${ADMIN_API}/order-product/page`, { params }),

  orderProductDetail: (id, playType) =>
    adminRequest.post(`${ADMIN_API}/order-product/detail`, { id }, { params: { playType } }),

  orderProductMarketSymbols: (params = {}) =>
    adminRequest.get(`${ADMIN_API}/order-product/market-symbols`, { params }),

  createOrderProduct: (data) =>
    adminRequest.post(`${ADMIN_API}/order-product`, data),

  updateOrderProduct: (data) =>
    adminRequest.put(`${ADMIN_API}/order-product`, data),

  listFuturesPeriods: (parentId) =>
    adminRequest.get(`${ADMIN_API}/order-product/futures-periods`, {
      params: { parentId },
    }),

  timedOrdersPage: (params) =>
    adminRequest.get(`${ADMIN_API}/timed-trade/orders/page`, { params }),

  exportTimedOrdersExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/timed-trade/orders/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'timed_order'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  listTimedDurations: (parentId) =>
    adminRequest.get(`${ADMIN_API}/order-product/timed-durations`, {
      params: { parentId },
    }),

  createTimedDuration: (data) =>
    adminRequest.post(`${ADMIN_API}/order-product/timed-durations`, data),

  updateTimedDuration: (data) =>
    adminRequest.put(`${ADMIN_API}/order-product/timed-durations`, data),

  deleteTimedDuration: (id) =>
    adminRequest.post(`${ADMIN_API}/order-product/timed-durations/delete`, { id }),

  perpetualOrdersPage: (params) =>
    adminRequest.get(`${ADMIN_API}/perpetual-contract/orders/page`, { params }),

  perpetualOrderDetail: (id) =>
    adminRequest.get(`${ADMIN_API}/perpetual-contract/orders/detail`, { params: { id } }),

  closePerpetualOrder: (id) =>
    adminRequest.post(`${ADMIN_API}/perpetual-contract/orders/close`, { id }),

  cancelPerpetualEntrustment: (id) =>
    adminRequest.post(`${ADMIN_API}/perpetual-contract/orders/cancel`, { id }),

  exportPerpetualOrdersExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/perpetual-contract/orders/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'perpetual_order'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  /** 交易控獎 */
  orderControlGlobal: (playType, productId) => {
    const params = { playType };
    if (productId != null && productId !== '') {
      params.productId = productId;
    }
    return adminRequest.get(`${ADMIN_API}/order-control/global`, { params });
  },

  saveOrderControlGlobal: (data) =>
    adminRequest.post(`${ADMIN_API}/order-control/global`, data),

  orderControlPersonalPage: (params) =>
    adminRequest.get(`${ADMIN_API}/order-control/personal/page`, { params }),

  saveOrderControlPersonal: (data) =>
    adminRequest.post(`${ADMIN_API}/order-control/personal`, data),

  updateOrderControlPersonal: (data) =>
    adminRequest.put(`${ADMIN_API}/order-control/personal`, data),

  deleteOrderControlPersonal: (id) =>
    adminRequest.post(`${ADMIN_API}/order-control/personal/delete`, { id }),

  /** 首頁今日統計（會員日報匯總） */
  statsDailyReportIndex: (params) =>
    adminRequest.post(`${ADMIN_API}/stats/member-daily-report/index`, null, {
      params,
    }),

  memberDailyReportPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/stats/member-daily-report/page-query`, { params }),

  exportMemberDailyReportExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/stats/member-daily-report/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      'member_daily_report.zip'
    );
    downloadBlob(blob, fileName);
  },

  imCsReportInterval: (params) =>
    adminRequest.get(`${ADMIN_API}/stats/im-cs-report/interval`, { params }),

  imChatReportInterval: (params) =>
    adminRequest.get(`${ADMIN_API}/stats/im-chat-report/interval`, { params }),

  getImFeatureSwitch: () =>
    adminRequest.get(`${ADMIN_API}/im/feature-switch`),

  updateImFeatureSwitch: (data) =>
    adminRequest.put(`${ADMIN_API}/im/feature-switch`, data),

  agentDailyReportPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/stats/agent-daily-report/page-query`, { params }),

  exportAgentDailyReportExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/stats/agent-daily-report/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      'agent_daily_report.zip'
    );
    downloadBlob(blob, fileName);
  },

  memberDailyStatisticalPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/member/daily-statistical/page-query`, { params }),

  exportMemberDailyStatisticalExcel: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/member/daily-statistical/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      'member_personal_statistical.zip'
    );
    downloadBlob(blob, fileName);
  },

  /** 待審充值筆數（線下／線上分開統計） */
  pendingDepositCounts: () =>
    Promise.all([
      adminRequest.get(`${ADMIN_API}/memberDeposit/offline/page`, {
        params: { pageNo: 1, pageSize: 1, status: 'PENDING' },
      }),
      adminRequest.get(`${ADMIN_API}/memberDeposit/online/page`, {
        params: { pageNo: 1, pageSize: 1, status: 'PENDING' },
      }),
    ]).then(([offline, online]) => ({
      offline: offline?.total ?? 0,
      online: online?.total ?? 0,
    })),

  /** 待審提現筆數 */
  pendingWithdrawCount: () =>
    adminRequest
      .get(`${ADMIN_API}/memberWithdraw/page`, {
        params: { pageNo: 1, pageSize: 1, status: 'PENDING' },
      })
      .then((data) => data?.total ?? 0),

  /** KYC 身份認證分頁 */
  kycPage: (pageNo, pageSize, body) =>
    adminRequest.post(`${ADMIN_API}/member/kyc/page`, body || {}, {
      params: { pageNo, pageSize },
    }),

  kycDetail: (memberId) =>
    adminRequest.get(`${ADMIN_API}/member/kyc/detail`, { params: { memberId } }),

  kycUpdate: (data) =>
    adminRequest.post(`${ADMIN_API}/member/kyc/update`, data),

  kycAudit: (data) =>
    adminRequest.post(`${ADMIN_API}/member/kyc/audit`, data),

  kycNumberVerification: (identityNumber, excludeMemberId) =>
    adminRequest.get(
      `${ADMIN_API}/member/kyc/number-verification/${encodeURIComponent(identityNumber)}`,
      { params: excludeMemberId ? { excludeMemberId } : {} }
    ),

  kycJourney: (memberId) =>
    adminRequest.get(`${ADMIN_API}/member/kyc/journey`, { params: { memberId } }),

  withdrawAddressPage: (params) =>
    adminRequest.get(`${ADMIN_API}/member/withdraw/page-query`, { params }),

  updateWithdrawAddress: (data) =>
    adminRequest.post(`${ADMIN_API}/member/withdraw/update`, data),

  adminRolePage: (params) =>
    adminRequest.get(`${ADMIN_API}/adminRole/page`, { params }),

  createAdminRole: (data) =>
    adminRequest.post(`${ADMIN_API}/adminRole/save`, data),

  updateAdminRole: (data) =>
    adminRequest.post(`${ADMIN_API}/adminRole/update`, data),

  removeAdminRoles: (ids) =>
    adminRequest.post(`${ADMIN_API}/adminRole/remove`, ids),

  listAdminPermissions: () =>
    adminRequest.get(`${ADMIN_API}/adminPermission/list`),

  adminStaffPage: (params) =>
    adminRequest.get(`${ADMIN_API}/info/page`, { params }),

  createAdminStaff: (data) =>
    adminRequest.post(`${ADMIN_API}/info/save`, data),

  updateAdminStaff: (data) =>
    adminRequest.post(`${ADMIN_API}/info/update`, data),

  removeAdminStaff: (ids) =>
    adminRequest.post(`${ADMIN_API}/info/remove`, ids),

  resetAdminStaffPassword: (data) =>
    adminRequest.post(`${ADMIN_API}/info/reset-password`, data),

  adminGooglePage: (params) =>
    adminRequest.get(`${ADMIN_API}/google/page`, { params }),

  resetAdminGoogleState: (data) =>
    adminRequest.post(`${ADMIN_API}/google/reset/googleState`, data),

  newsPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/news/page-query`, { params }),

  getNews: (id) =>
    adminRequest.get(`${ADMIN_API}/content/news/${id}`),

  newsLanguages: () =>
    adminRequest.get(`${ADMIN_API}/content/news/languages`),

  createNews: (data) =>
    adminRequest.post(`${ADMIN_API}/content/news`, data),

  updateNews: (data) =>
    adminRequest.put(`${ADMIN_API}/content/news`, data),

  deleteNews: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/news/${id}`),

  patchNewsState: (data) =>
    adminRequest.patch(`${ADMIN_API}/content/news/state`, data),

  newsReload: (providerKey) =>
    adminRequest.get(`${ADMIN_API}/content/news/newsReload`, {
      params: providerKey ? { providerKey } : {},
    }),

  exportWithdrawAddressCsvZip: async (params) => {
    const token = localStorage.getItem(ADMIN_STORAGE_KEYS.TOKEN);
    const response = await axios.get(`${ADMIN_API}/member/withdraw/out-excel`, {
      params,
      headers: token ? { Authorization: token } : {},
      responseType: 'blob',
    });
    const blob = response.data;
    if (blob?.type?.includes('application/json')) {
      const text = await blob.text();
      let payload;
      try {
        payload = JSON.parse(text);
      } catch {
        throw new ApiError('匯出失敗', undefined, text);
      }
      throw new ApiError(payload.message || '匯出失敗', payload.code, payload);
    }
    const fileName = parseContentDispositionFileName(
      response.headers['content-disposition'],
      `${params.fileName || 'member_withdraw_address'}.zip`
    );
    downloadBlob(blob, fileName);
  },

  verifyCodeRecordPage: (params) =>
    adminRequest.get(`${ADMIN_API}/verifyCodeRecord/page-query`, { params }),

  createDisposableCommonVerifyCode: () =>
    adminRequest.post(`${ADMIN_API}/verifyCodeRecord/common-verify-code/disposable`),

  createReusableCommonVerifyCode: () =>
    adminRequest.post(`${ADMIN_API}/verifyCodeRecord/common-verify-code/reusable`),

  sysDictionaryPage: (params) =>
    adminRequest.get(`${ADMIN_API}/system/sys-dic/page-query`, { params }),

  addSysDictionary: (data) =>
    adminRequest.post(`${ADMIN_API}/system/sys-dic/add`, data),

  updateSysDictionary: (data) =>
    adminRequest.post(`${ADMIN_API}/system/sys-dic/update`, data),

  deleteSysDictionary: (ids) =>
    adminRequest.post(`${ADMIN_API}/system/sys-dic/deletes`, { ids }),

  operationLogPage: (params) =>
    adminRequest.get(`${ADMIN_API}/system/access-log/page-query`, { params }),

  listSmsConfigs: () =>
    adminRequest.get(`${ADMIN_API}/system/sms/config/all`),

  smsContentTypes: () =>
    adminRequest.get(`${ADMIN_API}/system/sms/config/sms-content-type`),

  addSmsConfig: (data) =>
    adminRequest.post(`${ADMIN_API}/system/sms/config/add`, data),

  updateSmsConfig: (data) =>
    adminRequest.post(`${ADMIN_API}/system/sms/config/update`, data),

  deleteSmsConfig: (providerKey) =>
    adminRequest.get(`${ADMIN_API}/system/sms/config/delete`, {
      params: { providerKey },
    }),

  carouselPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/carousel/page-query`, { params }),

  getCarousel: (id) =>
    adminRequest.get(`${ADMIN_API}/content/carousel/${id}`),

  createCarousel: (data) =>
    adminRequest.post(`${ADMIN_API}/content/carousel`, data),

  updateCarousel: (data) =>
    adminRequest.put(`${ADMIN_API}/content/carousel`, data),

  deleteCarousel: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/carousel/${id}`),

  imStickerPackPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/sticker/packs/page-query`, { params }),

  getImStickerPack: (id) =>
    adminRequest.get(`${ADMIN_API}/sticker/packs/${id}`),

  createImStickerPack: (data) =>
    adminRequest.post(`${ADMIN_API}/sticker/packs`, data),

  updateImStickerPack: (id, data) =>
    adminRequest.put(`${ADMIN_API}/sticker/packs/${id}`, data),

  listImStickers: (packId) =>
    adminRequest.get(`${ADMIN_API}/sticker/packs/${packId}/stickers`),

  createImSticker: (packId, data) =>
    adminRequest.post(`${ADMIN_API}/sticker/packs/${packId}/stickers`, data),

  getImSticker: (id) =>
    adminRequest.get(`${ADMIN_API}/sticker/stickers/${id}`),

  updateImSticker: (id, data) =>
    adminRequest.put(`${ADMIN_API}/sticker/stickers/${id}`, data),

  imGifPackPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/sticker/gif/packs/page-query`, { params }),

  getImGifPack: (id) =>
    adminRequest.get(`${ADMIN_API}/sticker/gif/packs/${id}`),

  createImGifPack: (data) =>
    adminRequest.post(`${ADMIN_API}/sticker/gif/packs`, data),

  updateImGifPack: (id, data) =>
    adminRequest.put(`${ADMIN_API}/sticker/gif/packs/${id}`, data),

  listImGifs: (packId) =>
    adminRequest.get(`${ADMIN_API}/sticker/gif/packs/${packId}/gifs`),

  createImGif: (packId, data) =>
    adminRequest.post(`${ADMIN_API}/sticker/gif/packs/${packId}/gifs`, data),

  getImGif: (id) =>
    adminRequest.get(`${ADMIN_API}/sticker/gif/gifs/${id}`),

  updateImGif: (id, data) =>
    adminRequest.put(`${ADMIN_API}/sticker/gif/gifs/${id}`, data),

  imReactionPackPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/reaction/packs/page-query`, { params }),

  getImReactionPack: (id) =>
    adminRequest.get(`${ADMIN_API}/reaction/packs/${id}`),

  createImReactionPack: (data) =>
    adminRequest.post(`${ADMIN_API}/reaction/packs`, data),

  updateImReactionPack: (id, data) =>
    adminRequest.put(`${ADMIN_API}/reaction/packs/${id}`, data),

  listImReactionEmojis: (packId) =>
    adminRequest.get(`${ADMIN_API}/reaction/packs/${packId}/emojis`),

  createImReactionEmoji: (packId, data) =>
    adminRequest.post(`${ADMIN_API}/reaction/packs/${packId}/emojis`, data),

  updateImReactionEmoji: (id, data) =>
    adminRequest.put(`${ADMIN_API}/reaction/emojis/${id}`, data),

  imReportPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/im/reports/page-query`, { params }),

  getImReport: (id) =>
    adminRequest.get(`${ADMIN_API}/im/reports/${id}`),

  updateImReport: (id, data) =>
    adminRequest.put(`${ADMIN_API}/im/reports/${id}`, data),

  listImViolations: (memberId) =>
    adminRequest.get(`${ADMIN_API}/im/reports/violations`, { params: { memberId } }),

  marqueePageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/marquee/page-query`, { params }),

  getMarquee: (id) =>
    adminRequest.get(`${ADMIN_API}/content/marquee/${id}`),

  createMarquee: (data) =>
    adminRequest.post(`${ADMIN_API}/content/marquee`, data),

  updateMarquee: (data) =>
    adminRequest.put(`${ADMIN_API}/content/marquee`, data),

  deleteMarquee: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/marquee/${id}`),

  getDynamicMarqueeSwitch: () =>
    adminRequest.get(`${ADMIN_API}/content/marquee/getDynamicMarqueeSwitch`),

  setDynamicMarqueeSwitch: (data) =>
    adminRequest.post(`${ADMIN_API}/content/marquee/setDynamicMarqueeSwitch`, data),

  getPop: () =>
    adminRequest.get(`${ADMIN_API}/content/pop`),

  updatePop: (data) =>
    adminRequest.put(`${ADMIN_API}/content/pop`, data),

  announcementCenterPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/announcement-center/page-query`, { params }),

  getAnnouncementCenter: (id) =>
    adminRequest.get(`${ADMIN_API}/content/announcement-center/${id}`),

  createAnnouncementCenter: (data) =>
    adminRequest.post(`${ADMIN_API}/content/announcement-center`, data),

  updateAnnouncementCenter: (data) =>
    adminRequest.put(`${ADMIN_API}/content/announcement-center`, data),

  updateAnnouncementCenterState: (data) =>
    adminRequest.patch(`${ADMIN_API}/content/announcement-center/state`, data),

  deleteAnnouncementCenter: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/announcement-center/${id}`),

  helpCenterPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/help-center/page-query`, { params }),

  getHelpCenter: (id) =>
    adminRequest.get(`${ADMIN_API}/content/help-center/${id}`),

  createHelpCenter: (data) =>
    adminRequest.post(`${ADMIN_API}/content/help-center`, data),

  updateHelpCenter: (data) =>
    adminRequest.put(`${ADMIN_API}/content/help-center`, data),

  deleteHelpCenter: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/help-center/${id}`),

  getAboutUs: () =>
    adminRequest.get(`${ADMIN_API}/content/about`),

  updateAboutUs: (data) =>
    adminRequest.put(`${ADMIN_API}/content/about`, data),

  getAgreement: () =>
    adminRequest.get(`${ADMIN_API}/content/agreement`),

  updateAgreement: (data) =>
    adminRequest.put(`${ADMIN_API}/content/agreement`, data),

  getPrivacyPolicy: () =>
    adminRequest.get(`${ADMIN_API}/content/privacy-policy`),

  updatePrivacyPolicy: (data) =>
    adminRequest.put(`${ADMIN_API}/content/privacy-policy`, data),

  getBeginnerGuide: () =>
    adminRequest.get(`${ADMIN_API}/content/beginnerGuide`),

  updateBeginnerGuide: (data) =>
    adminRequest.put(`${ADMIN_API}/content/beginnerGuide`, data),

  getShowBeginnerGuideSwitch: () =>
    adminRequest.get(`${ADMIN_API}/content/beginnerGuide/getShowBeginnerGuideSwitch`),

  setShowBeginnerGuideSwitch: (data) =>
    adminRequest.post(`${ADMIN_API}/content/beginnerGuide/setShowBeginnerGuideSwitch`, data),

  customerServiceTypePageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-type/page-query`, { params }),

  getCustomerServiceType: (id) =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-type/${id}`),

  customerServiceTypeOptions: (params) =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-type/option`, { params }),

  createCustomerServiceType: (data) =>
    adminRequest.post(`${ADMIN_API}/content/customer-service-type`, data),

  updateCustomerServiceType: (data) =>
    adminRequest.put(`${ADMIN_API}/content/customer-service-type`, data),

  updateCustomerServiceTypeState: (data) =>
    adminRequest.patch(`${ADMIN_API}/content/customer-service-type/state`, data),

  deleteCustomerServiceType: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/customer-service-type/${id}`),

  customerServiceManagementPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-management/page-query`, { params }),

  getCustomerServiceManagement: (id) =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-management/${id}`),

  customerServiceOnlineProviders: () =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-management/option/online/provider`),

  createCustomerServiceManagement: (data) =>
    adminRequest.post(`${ADMIN_API}/content/customer-service-management`, data),

  updateCustomerServiceManagement: (data) =>
    adminRequest.put(`${ADMIN_API}/content/customer-service-management`, data),

  updateCustomerServiceManagementState: (data) =>
    adminRequest.patch(`${ADMIN_API}/content/customer-service-management/state`, data),

  deleteCustomerServiceManagement: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/customer-service-management/${id}`),

  findCustomerImageConfig: () =>
    adminRequest.get(`${ADMIN_API}/content/customer-service-management/findCustomerImageConfig`),

  updateCustomerImageConfig: (data) =>
    adminRequest.post(`${ADMIN_API}/content/customer-service-management/updateCustomerImageConfig`, data),

  inboxPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/inbox/page-query`, { params }),

  getInbox: (id) =>
    adminRequest.get(`${ADMIN_API}/content/inbox/${id}`),

  createInbox: (data) =>
    adminRequest.post(`${ADMIN_API}/content/inbox`, data),

  updateInbox: (data) =>
    adminRequest.put(`${ADMIN_API}/content/inbox`, data),

  deleteInbox: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/inbox/${id}`),

  broadcastInboxPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/content/inbox/broadcast/page-query`, { params }),

  getBroadcastInbox: (id) =>
    adminRequest.get(`${ADMIN_API}/content/inbox/broadcast/${id}`),

  createBroadcastInbox: (data) =>
    adminRequest.post(`${ADMIN_API}/content/inbox/broadcast/addAndSend`, data),

  updateBroadcastInbox: (data) =>
    adminRequest.put(`${ADMIN_API}/content/inbox/broadcast/update`, data),

  deleteBroadcastInbox: (id) =>
    adminRequest.delete(`${ADMIN_API}/content/inbox/broadcast/${id}`),

  // IM Customer Service workbench (CS7)
  imCsAgentMe: () =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/agents/me`),

  imCsUpdatePresence: (data) =>
    adminRequest.put(`${ADMIN_API}/im/admin/customer-service/agents/me/presence`, data),

  imCsHeartbeat: (data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/agents/me/heartbeat`, data),

  imCsListAgents: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/agents`, { params }),

  imCsQueue: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/queue`, { params }),

  imCsSupervisorQueue: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/supervisor/queue`, { params }),

  imCsMySessions: () =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/agents/me/sessions`),

  imCsMyEndedSessions: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/agents/me/ended-sessions`, { params }),

  imCsSupervisorEndedSessions: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/supervisor/ended-sessions`, { params }),

  imCsSupervisorAuditLogs: (params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/supervisor/audit-logs`, { params }),

  imCsAuditSummary: (sessionId) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/audit-summary`),

  imCsClaim: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/claim`, data || {}),

  imCsListMessages: (sessionId, params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/messages`, { params }),

  imCsSendMessage: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/messages`, data),

  imCsEndSession: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/end`, data || {}),

  imCsInviteRating: (sessionId) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/rating-prompt`),

  imCsTransferCandidates: (sessionId, params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/transfer-candidates`, { params }),

  imCsCreateTransfer: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/transfers`, data),

  imCsAcceptTransfer: (transferId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/transfers/${transferId}/accept`, data || {}),

  imCsRejectTransfer: (transferId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/transfers/${transferId}/reject`, data || {}),

  imCsCancelTransfer: (transferId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/transfers/${transferId}/cancel`, data || {}),

  imCsWatch: (sessionId) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/watch`),

  imCsUnwatch: (sessionId) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/unwatch`),

  imCsForceAssign: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/force-assign`, data),

  imCsForceRelease: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/force-release`, data || {}),

  imCsForceTransfer: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/force-transfer`, data),

  imCsSoftAssignCandidates: (sessionId, params) =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/soft-assign-candidates`, { params }),

  imCsCreateSoftAssign: (sessionId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/sessions/${sessionId}/soft-assigns`, data),

  imCsAcceptSoftAssign: (assignRequestId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/soft-assigns/${assignRequestId}/accept`, data || {}),

  imCsRejectSoftAssign: (assignRequestId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/soft-assigns/${assignRequestId}/reject`, data || {}),

  imCsCancelSoftAssign: (assignRequestId, data) =>
    adminRequest.post(`${ADMIN_API}/im/admin/customer-service/soft-assigns/${assignRequestId}/cancel`, data || {}),

  imCsPendingSoftAssigns: () =>
    adminRequest.get(`${ADMIN_API}/im/admin/customer-service/agents/me/pending-soft-assigns`),

  travelGroupTripPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/group-trips/page-query`, { params, ...travelAdminJson }),

  travelGroupTripDetail: (groupTripId) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/group-trips/${String(groupTripId)}`, travelAdminJson),

  travelTradePageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/trades/page-query`, { params, ...travelAdminJson }),

  travelTradeDetail: (tradeId) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/trades/${String(tradeId)}`, travelAdminJson),

  travelSafetyReportPageQuery: (params) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/safety/reports/page-query`, { params, ...travelAdminJson }),

  travelSafetyReportDetail: (reportId) =>
    adminRequest.get(`${ADMIN_API}/travel-manage/safety/reports/${String(reportId)}`, travelAdminJson),

  travelSafetyOpenCase: (data) =>
    adminRequest.post(`${ADMIN_API}/travel-manage/safety/moderation/cases`, data, travelAdminJson),

  travelSafetyDecideCase: (caseId, data) =>
    adminRequest.post(
      `${ADMIN_API}/travel-manage/safety/moderation/cases/${String(caseId)}/decide`,
      data,
      travelAdminJson,
    ),
};
